import { useState, useCallback, useEffect, useMemo } from 'react'
import { getBucketInfo } from '../utils/helpers/supabase.helpers'
import { useSessionContext } from '../contexts/SessionContext'
import { formatBytes, mimeTypeExtension } from '../utils/helpers/mime.helpers'

interface UseBucketInfoParams {
  bucket: string
  enabled?: boolean
}

interface BucketInfo {
  file_size_limit: number
  allowed_mime_types: string[]
}

interface UseBucketInfoReturn {
  isLoading: boolean
  error: Error | null
  uploadSizeLimit: number
  formattedUploadLimitInMB: string
  allowedFileTypes: string[]
  allowedFileExtensions: string[]
}

export const useBucketInfo = ({ bucket, enabled }: UseBucketInfoParams): UseBucketInfoReturn => {
  const { loading: loadingSession, user } = useSessionContext()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [uploadSizeLimit, setUploadSizeLimit] = useState<number>(0)
  const [allowedFileTypes, setAllowedFileTypes] = useState<string[]>([])

  const parseBucketInfo = useCallback(async (): Promise<void> => {
    if (enabled === false) {
      return
    }

    const { data, error: bucketError } = await getBucketInfo(bucket)

    if (bucketError) {
      setError(bucketError as Error)
      setIsLoading(false)
      return
    }

    const { file_size_limit, allowed_mime_types } = data as BucketInfo
    setUploadSizeLimit(file_size_limit)
    setAllowedFileTypes(allowed_mime_types)
    setError(null)
    setIsLoading(false)
  }, [enabled, bucket])

  useEffect(() => {
    if (loadingSession || !user?.id || enabled === false) {
      return
    }
    parseBucketInfo()
  }, [loadingSession, user, enabled, parseBucketInfo])

  const allowedFileExtensions = useMemo(
    () => allowedFileTypes?.map((mime) => mimeTypeExtension(mime)) ?? [],
    [allowedFileTypes]
  )

  const formattedUploadLimitInMB = formatBytes(uploadSizeLimit)

  return {
    isLoading,
    error,
    uploadSizeLimit,
    formattedUploadLimitInMB,
    allowedFileTypes,
    allowedFileExtensions,
  }
}
